/** Client-side report section exclusion (mirrors report_sections.py). */
import { PRETEXT_OVERVIEW_HEADING, parsePretextSectionHeading } from './reportPretext';

const AUTHORIZATION_SECTION_HEADING_RE = /^(?:authorization\s+record|authorized[\s-]?use\s+record|consent\s+record|legal\s+authorization|export\s+authorization|deliverable\s+authorization)(?:\s|:|$)/i;

export function isAuthorizationReportSection(section) {
  if (!section) return false;
  const heading = (section.heading || '').trim();
  if (heading && AUTHORIZATION_SECTION_HEADING_RE.test(heading)) {
    return true;
  }
  const body = (section.body || '').trim().toLowerCase();
  if (!body) return false;
  const markers = [
    'authorization record',
    'lifecycle status:',
    'deliverable status:',
    'consent recorded:',
    'this deliverable was generated under recorded authorization',
  ];
  const hits = markers.filter((marker) => body.includes(marker)).length;
  return hits >= 2;
}

export function stripAuthorizationFromReportData(reportData) {
  if (!reportData) return reportData;
  const filtered = JSON.parse(JSON.stringify(reportData));
  delete filtered.authorization_block;
  filtered.sections = (filtered.sections || []).filter(
    (section) => !isAuthorizationReportSection(section),
  );
  return filtered;
}

export const REPORT_SECTION_OPTIONS = [
  { id: 'executive_summary', label: 'Executive Summary', description: 'High-level overview and strategic summary.' },
  { id: 'recommendations', label: 'Priority Recommendations', description: 'Top prioritized remediation actions.' },
  { id: 'defensive_recommendations', label: 'Defensive Recommendations', description: 'Defense-focused recommendations by section.' },
  { id: 'pretexts', label: 'Pretexts & Scripts', description: 'Developed scenarios, scripts, and pretext content.' },
  { id: 'campaign', label: 'Campaign Sequence', description: 'Campaign sequencing and operational flow sections.' },
  { id: 'findings', label: 'Findings / Topic Sections', description: 'Core body findings and topic analyses.' },
  { id: 'vulnerabilities', label: 'Vulnerabilities', description: 'Structured vulnerability inventory.' },
  { id: 'appendix', label: 'Appendix / Sources', description: 'Appendices, supporting context, and source summaries.' },
  { id: 'disclaimer', label: 'Disclaimer', description: 'Final legal and usage notice.' },
];

export function classifyReportSection(section) {
  if (!section) return 'findings';
  const heading = (section.heading || '').trim().toLowerCase();
  if (section.pretext_id) return 'pretexts';
  if (heading === PRETEXT_OVERVIEW_HEADING.toLowerCase()) return 'pretexts';
  if (heading.startsWith('pretext ')) return 'pretexts';
  if (heading.startsWith('scenario ') || heading.startsWith('attack scenario ')) return 'pretexts';
  if (parsePretextSectionHeading(section.heading)) return 'pretexts';
  if (heading.includes('pretext') && (heading.includes('script') || heading.includes('scenario'))) {
    return 'pretexts';
  }
  if (heading.includes('campaign sequence') || heading.startsWith('campaign')) return 'campaign';
  if (heading.includes('appendix') || heading.includes('source document') || heading.includes('uploaded file')) {
    return 'appendix';
  }
  if (heading.includes('defensive') && heading.includes('recommendation')) {
    return 'defensive_recommendations';
  }
  if (heading.includes('vulnerabilit') || heading.includes('risk finding')) return 'vulnerabilities';
  return 'findings';
}

export function filterReportByExcludedSections(reportData, excludedSections) {
  if (!reportData || !excludedSections?.length) {
    return reportData;
  }
  const excluded = new Set(excludedSections);
  const filtered = JSON.parse(JSON.stringify(reportData));

  if (excluded.has('executive_summary')) {
    filtered.executive_summary = '';
  }
  if (excluded.has('recommendations')) {
    filtered.recommendations = [];
  }
  if (excluded.has('vulnerabilities')) {
    filtered.vulnerabilities = [];
  }
  if (excluded.has('disclaimer')) {
    filtered.disclaimer = '';
  }

  filtered.sections = (filtered.sections || []).filter(
    (section) => !excluded.has(classifyReportSection(section)),
  );

  return filtered;
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function getReportSectionAvailability(reportData, sectionId) {
  if (!reportData) return false;
  const sections = reportData.sections || [];
  switch (sectionId) {
    case 'executive_summary':
      return hasText(reportData.executive_summary);
    case 'recommendations':
      return Array.isArray(reportData.recommendations) && reportData.recommendations.length > 0;
    case 'vulnerabilities':
      return Array.isArray(reportData.vulnerabilities) && reportData.vulnerabilities.length > 0;
    case 'disclaimer':
      return hasText(reportData.disclaimer);
    default:
      return sections.some((section) => classifyReportSection(section) === sectionId);
  }
}

export function hasExportableReportContent(reportData) {
  if (!reportData) return false;
  return REPORT_SECTION_OPTIONS.some((option) => {
    if (option.id === 'disclaimer') return false;
    return getReportSectionAvailability(reportData, option.id);
  });
}
